import {
  ArrowRight, Bell, Building2, FileDown, FileSearch, Gavel, Globe2, Landmark,
  MapPin, Radar, Scale, ShieldCheck, Sparkles, X,
} from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import GrowthChart from './GrowthChart';
import WorldDots from './WorldDots';
import { fmt, quantity, useCorpusFacts } from './useCorpusFacts';
import styles from './Home1Page.module.css';

/**
 * A second public home page, at "/home1" (SRS #56).
 *
 * KN asked for an alternative built to the language of zoho.com/one and
 * quorum.us, to be put beside "/home" and chosen between. Both of those were
 * read rather than remembered, and what they actually share is:
 *
 *   Quorum   a dismissible announcement bar; a soft tinted hero rather than a
 *            dark one; a pill badge over a very large left-aligned headline;
 *            two fully-rounded CTAs; a rating strip; a radial hub diagram with
 *            labelled satellites; vertical pill lists beside a product mock.
 *   Zoho One colour-blocked bands, each section a different warm tint; huge
 *            stat numbers separated by thin rules; alternating text-and-
 *            screenshot splits, each with a coloured "learn more" link and a
 *            row of small labelled icons.
 *
 * So this page is light and colour-blocked where "/home" is one dark ground —
 * which is the point of having both. The content and every figure are
 * Policyora's own, read from the same hook "/home" uses so the two can never
 * disagree about the corpus.
 */

/** The four jurisdiction types, which is what the hub actually plots.
 *  Chosen over invented categories because it also answers the question KN
 *  keeps being asked: why the list shows a country on one line and a state on
 *  the next. */
const HUB = [
  { key: 'federal', icon: Landmark, label: 'Federal', note: 'A national legislature' },
  { key: 'state', icon: MapPin, label: 'State', note: 'A state or province' },
  { key: 'country', icon: Globe2, label: 'Country', note: 'A country in its own right' },
  { key: 'supranational', icon: Scale, label: 'Bloc', note: 'The European Union' },
] as const;

const BANDS = [
  {
    tone: 'sand',
    eyebrow: 'Track',
    title: 'Every instrument, in the places you operate',
    body: 'Bills, acts, rules, guidance and executive orders, collected from the legislatures '
      + 'themselves and filtered to the jurisdictions you chose. Nothing outside your scope '
      + 'reaches you.',
    cta: 'See what is covered',
    to: '/pricing',
    icons: [
      { icon: FileSearch, label: 'Search' },
      { icon: Radar, label: 'Alerts' },
      { icon: Bell, label: 'Key dates' },
    ],
  },
  {
    tone: 'mint',
    eyebrow: 'Assess',
    title: 'Where you stand, obligation by obligation',
    body: 'Bring the policies you already have. An assessment reads a regulation’s '
      + 'obligations one at a time and marks each met, partial or a gap — against your '
      + 'documents, not a questionnaire.',
    cta: 'How an assessment runs',
    to: '/faq',
    icons: [
      { icon: ShieldCheck, label: 'Governance' },
      { icon: Gavel, label: 'Compliance' },
      { icon: Building2, label: 'Risk' },
    ],
  },
  {
    tone: 'lilac',
    eyebrow: 'Report',
    title: 'Evidence attached, every time',
    body: 'Export the position as a report for the board, or a legal memo whose every citation '
      + 'has been checked back against the instrument it came from. An answer that cannot be '
      + 'traced is refused rather than guessed.',
    cta: 'Read the questions we are asked',
    to: '/faq',
    icons: [
      { icon: FileDown, label: 'Reports' },
      { icon: Scale, label: 'Memos' },
      { icon: Sparkles, label: 'Citations' },
    ],
  },
] as const;

/** The capabilities list beside the product frame — Quorum's vertical pills. */
const PILLS = [
  'Track an instrument from first draft to in force',
  'Filter every panel to one scope at once',
  'Assess your policies against a regulation',
  'Ask in plain language and check the citation',
  'Alert the team before a duty commences',
  'Export a board report or a legal memo',
];

const OBLIGATIONS: { label: string; state: 'met' | 'partial' | 'gap' }[] = [
  { label: 'Risk-management system documented', state: 'met' },
  { label: 'Human oversight assigned and recorded', state: 'met' },
  { label: 'Technical documentation retained', state: 'partial' },
  { label: 'Post-market monitoring plan', state: 'gap' },
];

export const Home1Page: React.FC = () => {
  const { stats, top, byCountry, blocs, trend, plans } =
    useCorpusFacts('Policyora — AI regulation, tracked and assessed');
  const [noticeOpen, setNoticeOpen] = useState(true);
  const busiest = top[0]?.count || 1;

  return (
    <div className={styles.page}>
      {/* Quorum opens on a dismissible announcement bar. Ours carries a fact
          rather than a slogan, and it goes away for good when closed. */}
      {noticeOpen && (
        <div className={styles.notice}>
          <span className={styles.noticeText}>
            {fmt(stats?.jurisdictions)} jurisdictions now tracked, across {fmt(stats?.countries)} countries.
          </span>
          <Link to="/pricing" className={styles.noticeCta}>
            See the plans <ArrowRight size={13} aria-hidden />
          </Link>
          <button
            type="button"
            className={styles.noticeClose}
            onClick={() => setNoticeOpen(false)}
            aria-label="Dismiss announcement"
          >
            <X size={15} aria-hidden />
          </button>
        </div>
      )}

      <header className={styles.topBar}>
        <div className={`${styles.wrap} ${styles.nav}`}>
          <img src="/Policy Ora Final Logo.png" alt="Policyora" className={styles.navLogo} />
          <span className={styles.navSpacer} />
          <Link to="/pricing" className={styles.navLink}>Pricing</Link>
          <Link to="/faq" className={styles.navLink}>FAQs</Link>
          <Link to="/login" className={styles.navLink}>Log in</Link>
          <Link to="/signup" className={styles.ctaPrimary}>Book a demo</Link>
        </div>
      </header>

      <main>
        {/* ── hero: soft tinted ground, not a dark one ─────────────────── */}
        <section className={styles.hero} aria-label="Introduction">
          <div className={`${styles.wrap} ${styles.heroGrid}`}>
            <div>
              <span className={styles.badge}>AI regulation, tracked and assessed</span>
              <h1 className={styles.headline}>
                Every rule that applies to you.
                <span className={styles.headlineAccent}> And where you stand.</span>
              </h1>
              <p className={styles.sub}>
                Policyora tracks AI and technology regulation across {fmt(stats?.jurisdictions)} jurisdictions,
                then checks your own policies against it — obligation by obligation, with the
                evidence attached.
              </p>
              <div className={styles.ctaRow}>
                <Link to="/signup" className={styles.ctaPrimary}>Book a demo</Link>
                <Link to="/pricing" className={styles.ctaSecondary}>Start free</Link>
              </div>
              <ul className={styles.ticks}>
                <li>Free to begin, no card required</li>
                <li>Every answer cites its source</li>
                <li>Scoped to the places you operate</li>
              </ul>
            </div>

            {/* The product, drawn rather than photographed, in a window. */}
            <div className={styles.window} aria-hidden="true">
              <div className={styles.windowBar}>
                <span className={styles.dot} /><span className={styles.dot} /><span className={styles.dot} />
                <span className={styles.windowTitle}>Assessment · EU AI Act</span>
              </div>
              <div className={styles.windowBody}>
                <div className={styles.meter}>
                  <span className={styles.meterMet} style={{ width: '58%' }} />
                  <span className={styles.meterPartial} style={{ width: '19%' }} />
                </div>
                <div className={styles.meterKey}>
                  <span><i className={`${styles.keyDot} ${styles.met}`} />18 met</span>
                  <span><i className={`${styles.keyDot} ${styles.partial}`} />6 partial</span>
                  <span><i className={`${styles.keyDot} ${styles.gap}`} />7 gaps</span>
                </div>
                {OBLIGATIONS.map((o) => (
                  <div key={o.label} className={styles.oblRow}>
                    <span className={styles.oblLabel}>{o.label}</span>
                    <span className={`${styles.pill} ${styles[o.state]}`}>
                      {o.state === 'met' ? 'Met' : o.state === 'partial' ? 'Partial' : 'Gap'}
                    </span>
                  </div>
                ))}
                <p className={styles.windowFoot}>
                  Each row carries the passage it was judged on.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── the figures, as Zoho sets them: large, divided ───────────── */}
        <section className={`${styles.wrap} ${styles.statBand}`} aria-label="Coverage in numbers">
          {[
            { v: stats?.regulations, l: 'Regulations tracked' },
            { v: stats?.jurisdictions, l: 'Jurisdictions covered' },
            { v: stats?.countries, l: 'Countries represented' },
          ].map((s) => (
            <div key={s.l} className={styles.stat}>
              <div className={styles.statValue}>{fmt(s.v)}</div>
              <div className={styles.statLabel}>{s.l}</div>
            </div>
          ))}
        </section>

        {/* ── the hub: one centre, four kinds of law-making body ───────── */}
        <section className={styles.hubBand} aria-label="What a jurisdiction is">
          <div className={styles.wrap}>
            <div className={styles.centred}>
              <span className={styles.badgeDark}>One corpus</span>
              <h2 className={styles.h2Dark}>
                Four kinds of law-maker, in one place
              </h2>
              <p className={styles.subDark}>
                A regulation always belongs to the body that made it. That body may be a country,
                a part of one, or a bloc — which is why a list of them shows the United Kingdom on
                one line and Illinois on the next.
              </p>
            </div>

            <div className={styles.hub}>
              <div className={styles.hubCore}>
                <Globe2 size={26} aria-hidden />
                <span className={styles.hubCoreValue}>{fmt(stats?.regulations)}</span>
                <span className={styles.hubCoreLabel}>regulations</span>
              </div>
              {HUB.map(({ key, icon: Icon, label, note }) => (
                <div key={key} className={`${styles.spoke} ${styles[key]}`}>
                  <span className={styles.spokeIcon}><Icon size={18} aria-hidden /></span>
                  <span className={styles.spokeLabel}>{label}</span>
                  <span className={styles.spokeNote}>{note}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Zoho's colour-blocked alternating bands ──────────────────── */}
        {BANDS.map((band, i) => (
          <section
            key={band.eyebrow}
            className={`${styles.band} ${styles[band.tone]}`}
            aria-label={band.eyebrow}
          >
            <div className={`${styles.wrap} ${styles.split} ${i % 2 ? styles.splitReverse : ''}`}>
              <div className={styles.splitText}>
                <p className={styles.eyebrow}>{band.eyebrow}</p>
                <h2 className={styles.h2}>{band.title}</h2>
                <p className={styles.body}>{band.body}</p>
                <Link to={band.to} className={styles.learnMore}>
                  {band.cta} <ArrowRight size={15} aria-hidden />
                </Link>
                <div className={styles.iconRow}>
                  {band.icons.map(({ icon: Icon, label }) => (
                    <span key={label} className={styles.iconChip}>
                      <Icon size={17} aria-hidden />
                      <em>{label}</em>
                    </span>
                  ))}
                </div>
              </div>

              <div className={styles.splitArt}>
                {i === 0 && Object.keys(byCountry).length > 0 && (
                  <WorldDots byCountry={byCountry} blocs={blocs} />
                )}
                {i === 1 && (
                  <ul className={styles.pillList}>
                    {PILLS.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                )}
                {i === 2 && trend.length > 1 && <GrowthChart data={trend} tone="light" />}
                {i === 2 && top.length > 0 && (
                  <div className={styles.leaders}>
                    {top.slice(0, 4).map((j) => (
                      <div key={j.name} className={styles.leaderRow}>
                        <span className={styles.leaderName}>{j.name}</span>
                        <span className={styles.leaderTrack}>
                          <span
                            className={styles.leaderFill}
                            style={{ width: `${Math.max(6, (j.count / busiest) * 100)}%` }}
                          />
                        </span>
                        <span className={styles.leaderCount}>{fmt(j.count)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* ── plans, from the catalogue ────────────────────────────────── */}
        {plans.length > 0 && (
          <section className={`${styles.wrap} ${styles.section}`} aria-label="Plans">
            <div className={styles.centred}>
              <p className={styles.eyebrow}>Plans</p>
              <h2 className={styles.h2}>Priced by how much of the world you watch</h2>
            </div>
            <div className={styles.planRow}>
              {plans.map((pl) => (
                <article
                  key={pl.tier}
                  className={`${styles.plan} ${pl.is_popular ? styles.planPopular : ''}`}
                >
                  {pl.is_popular && <span className={styles.planFlag}>Most chosen</span>}
                  <h3 className={styles.planName}>{pl.display_name}</h3>
                  <p className={styles.planPrice}>
                    {pl.contact_sales
                      ? <span className={styles.planContact}>Talk to us</span>
                      : (
                        <>
                          <span className={styles.planAmount}>
                            ${Math.round(Number(pl.monthly_price_usd ?? 0))}
                          </span>
                          <span className={styles.planPer}>/month</span>
                        </>
                      )}
                  </p>
                  <p className={styles.planMeta}>
                    {quantity(pl.features?.jurisdictions, 'jurisdiction', 'jurisdictions')}
                    {' · '}
                    {quantity(pl.features?.user_seats, 'seat', 'seats')}
                  </p>
                  <Link to="/pricing" className={styles.planLink}>
                    What is included <ArrowRight size={14} aria-hidden />
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ── closing ──────────────────────────────────────────────────── */}
        <section className={styles.closing} aria-label="Get started">
          <div className={styles.wrap}>
            <h2 className={styles.h2Dark}>Start with your jurisdictions.</h2>
            <p className={styles.subDark}>
              Free to begin, no card required. Pick the places you operate in and the product
              narrows to them.
            </p>
            <div className={styles.ctaRow}>
              <Link to="/signup" className={styles.ctaLight}>Create an account</Link>
              <Link to="/pricing" className={styles.ctaGhost}>Compare plans</Link>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footerOuter}>
        <div className={`${styles.wrap} ${styles.footerGrid}`}>
          <div className={styles.footerBrand}>
            <img src="/Policy Ora Final Logo.png" alt="Policyora" className={styles.footerLogo} />
            <p className={styles.footerBlurb}>
              AI and technology regulation across {fmt(stats?.jurisdictions)} jurisdictions, and
              where your organisation stands against it.
            </p>
          </div>
          <nav className={styles.footerCol} aria-label="Product">
            <p className={styles.footerHead}>Product</p>
            <Link to="/pricing" className={styles.footerLink}>Pricing</Link>
            <Link to="/live-updates" className={styles.footerLink}>Live updates</Link>
          </nav>
          <nav className={styles.footerCol} aria-label="Support">
            <p className={styles.footerHead}>Support</p>
            <Link to="/faq" className={styles.footerLink}>FAQs</Link>
            <a href="mailto:sales@policyora.ai" className={styles.footerLink}>sales@policyora.ai</a>
          </nav>
          <nav className={styles.footerCol} aria-label="Account">
            <p className={styles.footerHead}>Account</p>
            <Link to="/login" className={styles.footerLink}>Log in</Link>
            <Link to="/signup" className={styles.footerLink}>Start free</Link>
          </nav>
        </div>
        <div className={`${styles.wrap} ${styles.footerBase}`}>
          <span>© {new Date().getFullYear()} Policyora. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
};

export default Home1Page;
